import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import BrandLogo from '@/components/BrandLogo';
import {
  LayoutDashboard, FileText, Image, BookOpen, User, Mail,
  Phone, Settings, LogOut, Menu, X, ChevronRight, Users, CreditCard, DollarSign, CalendarCheck
} from 'lucide-react';

export default function AdminLayout() {
  const { admin, logout, hasPermission, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const NAV_GROUPS = [
    {
      label: null,
      links: [
        { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true, show: true },
      ],
    },
    {
      label: 'Content',
      links: [
        { path: '/admin/content', label: 'Site Content', icon: FileText, show: hasPermission('content') },
        { path: '/admin/founder', label: 'Founder', icon: User, show: hasPermission('founder') },
        { path: '/admin/gallery', label: 'Gallery', icon: Image, show: hasPermission('gallery') },
        { path: '/admin/blog', label: 'Blog Posts', icon: BookOpen, show: hasPermission('blog') },
        { path: '/admin/contact', label: 'Contact Info', icon: Phone, show: hasPermission('contact') },
      ],
    },
    {
      label: 'People',
      links: [
        { path: '/admin/messages', label: 'Messages', icon: Mail, show: hasPermission('messages') },
        { path: '/admin/activity-registrations', label: 'Activity Registrations', icon: CalendarCheck, show: hasPermission('activity_registrations') },
        { path: '/admin/users', label: 'User Management', icon: Users, show: isSuperAdmin() },
      ],
    },
    {
      label: 'Finance',
      links: [
        { path: '/admin/donations', label: 'Donations', icon: DollarSign, show: hasPermission('donations') },
        { path: '/admin/stripe', label: 'Stripe Settings', icon: CreditCard, show: isSuperAdmin() },
      ],
    },
    {
      label: 'Settings',
      links: [
        { path: '/admin/footer-settings', label: 'Footer Settings', icon: FileText, show: isSuperAdmin() },
        { path: '/admin/settings', label: 'Settings', icon: Settings, show: true },
      ],
    },
  ]
    .map((group) => ({ ...group, links: group.links.filter((link) => link.show) }))
    .filter((group) => group.links.length > 0);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (link) => {
    if (link.exact) return location.pathname === link.path;
    return location.pathname.startsWith(link.path);
  };

  const emailLocalPart = (admin?.email || '').split('@')[0] || '';
  const initials = (emailLocalPart.slice(0, 2) || 'AD').toUpperCase();
  const roleLabel = admin?.role === 'super_admin'
    ? 'Super Admin'
    : admin?.role
      ? admin.role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : 'Admin';

  return (
    <div className="mscm-admin-shell min-h-screen overflow-x-hidden">
      {/* Mobile / Tablet Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 border-b border-[#E2E8F0] h-14 flex items-center px-4 shadow-[0_10px_28px_rgba(11,31,77,0.06)] backdrop-blur-xl">
        <button
          data-testid="admin-sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#475569] transition-colors hover:bg-[#EBF2FA] hover:text-[#2D5BE3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D5BE3]/30"
          aria-label={sidebarOpen ? 'Close admin navigation' : 'Open admin navigation'}
          aria-expanded={sidebarOpen}
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <span className="ml-3 font-semibold text-[#0D1B4B] text-sm">MSCM Admin</span>
      </div>

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`mscm-admin-sidebar fixed top-0 left-0 bottom-0 w-64 z-40 transform transition-transform duration-200 lg:translate-x-0 lg:visible ${
          sidebarOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'
        }`}
      >
        <div className="mscm-admin-sidebar-brand flex flex-col">
          <Link to="/" className="flex items-center gap-3 p-5" data-testid="admin-home-link">
            <span className="mscm-admin-sidebar-logo-chip">
              <BrandLogo
                src="/logo.png"
                alt="Million Stars Community Ministry"
                variant="admin"
                frameClassName="shrink-0"
                imageClassName="h-7 md:h-7"
              />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">MSCM Admin</div>
              <div className="text-xs text-white/45">Management Panel</div>
            </div>
          </Link>
        </div>

        <nav className="p-3 flex flex-col gap-0.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 152px)' }}>
          {NAV_GROUPS.map((group, groupIndex) => (
            <div key={group.label || 'main'}>
              {groupIndex > 0 && <div className="mscm-admin-sidebar-divider" aria-hidden="true" />}
              {group.label && <p className="mscm-admin-nav-group-label">{group.label}</p>}
              {group.links.map((link) => {
                const Icon = link.icon;
                const active = isActive(link);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`admin-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSidebarOpen(false)}
                    className={`mscm-admin-nav-link flex items-center gap-3 px-3 py-2.5 rounded-[8px] text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
                      active ? 'mscm-admin-nav-link-active' : ''
                    }`}
                  >
                    <Icon size={18} />
                    <span className="truncate">{link.label}</span>
                    {active && <ChevronRight size={14} className="ml-auto shrink-0" />}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="mscm-admin-sidebar-footer absolute bottom-0 left-0 right-0 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="mscm-admin-user-avatar" aria-hidden="true">{initials}</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white" title={admin?.email}>{admin?.email}</p>
              <span className="mscm-admin-user-role mt-0.5">{roleLabel}</span>
            </div>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="mscm-admin-logout-btn mt-1 flex min-h-11 w-full items-center gap-3 rounded-[8px] px-3 py-2.5 text-sm font-semibold text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-[#0B1F4D]/30 z-30 backdrop-blur-[2px]"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main className="min-w-0 lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 md:p-8 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
